import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { PaymentMethodType } from '../../../models';
import { fromPaymentMethod } from '../../../store/rootReducer';

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 15,
    fontWeight: 700,
    color: '#16141d',
    margin: theme.spacing(3.75, 0, 1.5),
  },
  cardRow: {
    display: 'flex',
    gap: theme.spacing(1.75),
  },
  methodCard: {
    flex: 1,
    textAlign: 'left',
    padding: '15px 17px',
    border: '1.5px solid #e1e7e4',
    borderRadius: 14,
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'border-color .15s ease, background .15s ease',
    '&:hover': {
      borderColor: '#bfe8d6',
    },
  },
  methodCardSelected: {
    borderColor: '#0f8461',
    backgroundColor: '#f2fbf7',
    '&:hover': {
      borderColor: '#0f8461',
    },
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.25),
    color: '#16141d',
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #cdcadf',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    transition: 'all .15s ease',
  },
  radioSelected: {
    borderColor: '#0f8461',
    backgroundColor: '#0f8461',
  },
  radioDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: 'transparent',
  },
  radioDotSelected: {
    backgroundColor: '#fff',
  },
  methodName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#16141d',
    marginTop: 11,
  },
  methodHint: {
    fontSize: 12.5,
    color: '#73718a',
    marginTop: 2,
  },
}));

const CardIcon = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <path d="M2 10h20" />
  </svg>
);

const BankIcon = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10h16M5 10l7-4.5 7 4.5M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16" />
  </svg>
);

const CartIcon = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

interface MethodCardOption {
  type: PaymentMethodType;
  icon: JSX.Element;
  nameId: string;
  hintId: string;
}

/**
 * Payment method selector from the Payment & billing v1 design mock: one
 * selectable card per method with a radio dot, green selected state.
 */
export const PaymentMethodCards = (
  { showBankTransfer, handleSelectPaymentMethod }: {
    showBankTransfer: boolean;
    handleSelectPaymentMethod: (paymentMethod: PaymentMethodType) => void;
  },
) => {
  const classes = useStyles();
  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);

  const options: MethodCardOption[] = [
    {
      type: PaymentMethodType.CREDIT_CARD,
      icon: <CardIcon />,
      nameId: 'page.order.confirmation.method.card',
      hintId: 'page.order.confirmation.method.card.hint',
    },
    ...(showBankTransfer ? [{
      type: PaymentMethodType.BANK_TRANSFER,
      icon: <BankIcon />,
      nameId: 'page.order.confirmation.method.bank',
      hintId: 'page.order.confirmation.method.bank.hint',
    }] : []),
    ...(selectedPaymentMethod === PaymentMethodType.AWS_MARKETPLACE ? [{
      type: PaymentMethodType.AWS_MARKETPLACE,
      icon: <CartIcon />,
      nameId: 'page.order.confirmation.method.aws',
      hintId: 'page.order.confirmation.method.aws.hint',
    }] : []),
  ];

  return (
    <Box id="payment-method-cards">
      <Typography className={classes.sectionTitle}>
        <FormattedMessage id="billinginfo.payment_method" />
      </Typography>
      <Box className={classes.cardRow}>
        {options.map(option => {
          const selected = option.type === selectedPaymentMethod;
          return (
            <button
              key={option.type}
              type="button"
              className={`${classes.methodCard} ${selected ? classes.methodCardSelected : ''}`}
              onClick={() => handleSelectPaymentMethod(option.type)}
            >
              <Box className={classes.cardTop}>
                <Box component="span" sx={{ display: 'inline-flex' }}>{option.icon}</Box>
                <Box component="span" className={`${classes.radio} ${selected ? classes.radioSelected : ''}`}>
                  <Box component="span" className={`${classes.radioDot} ${selected ? classes.radioDotSelected : ''}`} />
                </Box>
              </Box>
              <Typography className={classes.methodName}>
                <FormattedMessage id={option.nameId} />
              </Typography>
              <Typography className={classes.methodHint}>
                <FormattedMessage id={option.hintId} />
              </Typography>
            </button>
          );
        })}
      </Box>
    </Box>
  );
};
